import { CommonFilterModel, CommonParams, CommonExportType } from '@/api/resources/common/shared/models/common.models'
import i18n from '@/lang/i18n'
import { StatisticalAnalysisDelayAndResponseTimesDetailRequest, StatisticalAnalysisDelayAndResponseTimesDetailRequestChart, StatisticalAnalysisDelayAndResponseTimesDetailRequestTable, StatisticalAnalysisDelayAndResponseTimesDossierBaseRequest, StatisticalAnalysisDelayAndResponseTimesDossierDetailExternalViewTable, StatisticalAnalysisDelayAndResponseTimesDossierDetailViewChart, StatisticalAnalysisDelayAndResponseTimesDossierDetailViewTable, StatisticalAnalysisDelayAndResponseTimesDossierResponseContentDelayStats, StatisticalAnalysisDelayAndResponseTimesDossierResponseContentStats, StatisticalAnalysisDelayAndResponseTimesDossierResponseContentTimeStats, StatisticalAnalysisDelayAndResponseTimesDossierResponseHeaders } from '@/modules/statistical-analysis-module/services/delay-and-response-times-dossier/delay-and-response-times-dossier.dto'
import { LaboratoryStatisticalAnalysisSidebarDateInput } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-sidebar-date-and-centers/statistical-analysis-sidebar-date-and-centers.model'
import { StatisticalAnalysisTemporalFilterEnum } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-temporal-filter/statistical-analysis-temporal-filter.model'
import { StatisticalAnalysisViewDetailChartData, StatisticalAnalysisViewDetailChartFilters } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-chart/statistical-analysis-view-detail-chart.model'
import { formatDatetimes, StatiscalAnalysisTypeCases, StatisticalAnalysisDelayAndResponseEnum, StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeModeSelectorEnum, StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeSelectorEnum, StatisticalAnalysisDossiersExternalInfo, StatisticalAnalysisDynamicObject, StatisticalAnalysisEnumsAndKeys } from '@/modules/statistical-analysis-module/shared/models/shared.model'
import { StatisticalAnalysisTimesheetItemSerie } from '@/modules/statistical-analysis-module/shared/services/statistical-analysis-timesheet/statistical-analysis-timesheet.dto'
import { StatisticalAnalysisDelaysAndResponseTimeDossierExternalInfo } from '@/modules/statistical-analysis-module/views/delays-and-response-time-dossier/delays-and-response-time-dossier.model'
import { CommonPersonnelResponse } from '@/api/services/common/personnel/common-personnel.dto'
import { Nullable } from '@/shared/types/app.types'
import axios, { AxiosResponse } from 'axios'
import { SeriesOptionsType } from 'highcharts'
import { chain, isNil, map } from 'lodash'
import { ReportGridRequest } from '@/api/services/common.dto'
import { StatisticalAnalysisSidebarItemCombo } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-sidebar/statistical-analysis-sidebar.models'
import { store } from '@/store/store'

class DelayAndResponseTimesDossierService {
    async getGeneralViewHeaders(
        typeSearch: StatiscalAnalysisTypeCases,
        baseRequest: StatisticalAnalysisDelayAndResponseTimesDossierBaseRequest
    ): Promise<StatisticalAnalysisDelayAndResponseTimesDossierResponseHeaders> {
        const response = await axios.get<StatisticalAnalysisDelayAndResponseTimesDossierResponseHeaders>(`/statisticAnalysis/dossiers/delayAndResponseTimes/${typeSearch?.toLocaleLowerCase()}`, { params: baseRequest })
        return response.data
    }

    async getGeneralViewContent(
        isDelay: boolean,
        typeSearch: StatiscalAnalysisTypeCases,
        baseRequest: StatisticalAnalysisDelayAndResponseTimesDossierBaseRequest,
        detailRequest: StatisticalAnalysisDelayAndResponseTimesDetailRequest
    ): Promise<StatisticalAnalysisDelayAndResponseTimesDossierResponseContentDelayStats[] | StatisticalAnalysisDelayAndResponseTimesDossierResponseContentTimeStats[]> {
        const params = { ...baseRequest, ...detailRequest }
        let response
        if (!isDelay) {
            response = await axios.get<StatisticalAnalysisDelayAndResponseTimesDossierResponseContentTimeStats[]>(`/statisticAnalysis/dossiers/delayAndResponseTimes/${typeSearch?.toLocaleLowerCase()}/responseTimeStats`, { params })
        } else {
            response = await axios.get<StatisticalAnalysisDelayAndResponseTimesDossierResponseContentDelayStats[]>(`/statisticAnalysis/dossiers/delayAndResponseTimes/${typeSearch?.toLocaleLowerCase()}/delayStats`, { params })
        }
        return this.parseGeneralViewContent(response.data)
    }

    parseGeneralViewContent(response: StatisticalAnalysisDelayAndResponseTimesDossierResponseContentStats[]) {
        return response.map((itemResponse: StatisticalAnalysisDelayAndResponseTimesDossierResponseContentStats) => {
            itemResponse.name = itemResponse.responseTimeType.translatedName
            return itemResponse
        })
    }

    async getDetailViewTable(
        filterModel: CommonFilterModel, commonParams: CommonParams,
        typeSearch: StatiscalAnalysisTypeCases, timeMeasureType: StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeSelectorEnum, responseTimeType: StatisticalAnalysisDelayAndResponseEnum,
        baseRequest: StatisticalAnalysisDelayAndResponseTimesDossierBaseRequest, detailRequest: StatisticalAnalysisDelayAndResponseTimesDetailRequestTable, entityType: Nullable<Object>,
        infoDetail: StatisticalAnalysisTemporalFilterEnum, scrollId: Nullable<string>): Promise<StatisticalAnalysisDelayAndResponseTimesDossierDetailViewTable> {
        let params = {
            ...filterModel,
            ...commonParams,
            ...baseRequest,
            ...detailRequest,
            ...entityType,
            ...{ infoDetail }
        }
        if (scrollId) {
            params = {
                ...params,
                ...{ scrollId }
            }
        }
        const response = await axios.get<StatisticalAnalysisDelayAndResponseTimesDossierDetailViewTable>(`/statisticAnalysis/dossiers/delayAndResponseTimes/${typeSearch?.toLowerCase()}/${timeMeasureType}/${responseTimeType}`, { params })
        return response.data
    }

    async getExternalViewDetailList(
        filterModel: CommonFilterModel, commonParams: CommonParams,
        typeCase: StatiscalAnalysisTypeCases, groupingParams: StatisticalAnalysisDynamicObject,
        entityTypeId: Nullable<number>, responseTimeType: StatisticalAnalysisDelayAndResponseEnum,
        delayType: StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeModeSelectorEnum, timeMeasureType: StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeSelectorEnum,
        baseRequest: StatisticalAnalysisDelayAndResponseTimesDossierBaseRequest, scrollId: Nullable<string>,
        totalElements: Nullable<number>
    ): Promise<StatisticalAnalysisDelayAndResponseTimesDossierDetailExternalViewTable> {
        let bodyParams = {
            ...groupingParams
        }
        let params = {
            ...filterModel,
            ...commonParams,
            ...baseRequest
        }
        if (!isNil(entityTypeId) && entityTypeId >= 0) {
            let entityType
            switch (typeCase) {
            case StatiscalAnalysisTypeCases.CASE:
                entityType = { caseTypeId: entityTypeId }
                break
            case StatiscalAnalysisTypeCases.SAMPLE:
                entityType = { sampleTypeId: entityTypeId }
                break
            case StatiscalAnalysisTypeCases.PORTION:
                entityType = { portionTypeId: entityTypeId }
                break
            case StatiscalAnalysisTypeCases.SUBPORTION:
                entityType = { subportionTypeId: entityTypeId }
                break
            default:
                entityType = {}
                break
            }
            params = {
                ...params,
                ...entityType
            }
        }

        if (timeMeasureType === StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeSelectorEnum.DELAY) {
            params = {
                ...params,
                ...{ delayType }
            }
        }

        if (!isNil(totalElements) && totalElements > 0) {
            params = {
                ...params,
                ...{ totalElements }
            }
        }

        if (scrollId) {
            params = {
                ...params,
                ...{ scrollId }
            }
        }
        const response = await axios.put<StatisticalAnalysisDelayAndResponseTimesDossierDetailExternalViewTable>(`/statisticAnalysis/dossiers/delayAndResponseTimes/${typeCase?.toLowerCase()}/${timeMeasureType}/${responseTimeType}/detailList`, bodyParams, { params })
        return response.data
    }

    async getDetailViewChart(
        typeSearch: StatiscalAnalysisTypeCases, timeMeasureType: StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeSelectorEnum,
        responseTimeType: StatisticalAnalysisDelayAndResponseEnum, baseRequest: StatisticalAnalysisDelayAndResponseTimesDossierBaseRequest,
        detailRequest: StatisticalAnalysisDelayAndResponseTimesDetailRequestChart, filters: StatisticalAnalysisViewDetailChartFilters,
        entityType: Nullable<Object>): Promise<StatisticalAnalysisViewDetailChartData> {
        const params = { ...baseRequest, ...detailRequest, ...entityType }
        const response = await axios.get<StatisticalAnalysisDelayAndResponseTimesDossierDetailViewChart[]>(`/statisticAnalysis/dossiers/delayAndResponseTimes/${typeSearch?.toLowerCase()}/${timeMeasureType}/${responseTimeType}/stats`, { params })
        return this._parseChartData(filters, response.data)
    }

    async getPersonnel(externalInfo: StatisticalAnalysisDossiersExternalInfo): Promise<CommonPersonnelResponse[]> {
        const externalInfoParsed = externalInfo as StatisticalAnalysisDelaysAndResponseTimeDossierExternalInfo
        const timeMeasureType = externalInfoParsed.typeDelaysAndResponseTimes!.id as StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeSelectorEnum
        const responseTimeType = externalInfoParsed.trackingDetailSelected!.key as StatisticalAnalysisDelayAndResponseEnum
        const delayType = externalInfoParsed.typeDelay as StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeModeSelectorEnum
        const entityTypeId: Nullable<number> = externalInfoParsed.trackingGeneralSelected?.key
        const entityType = (externalInfo.typeSearch as StatisticalAnalysisSidebarItemCombo).id

        let entityTypeSelected
        switch (entityType) {
        case StatiscalAnalysisTypeCases.CASE:
            entityTypeSelected = { caseTypeId: entityTypeId }
            break
        case StatiscalAnalysisTypeCases.SAMPLE:
            entityTypeSelected = { sampleTypeId: entityTypeId }
            break
        case StatiscalAnalysisTypeCases.PORTION:
            entityTypeSelected = { portionTypeId: entityTypeId }
            break
        case StatiscalAnalysisTypeCases.SUBPORTION:
            entityTypeSelected = { subportionTypeId: entityTypeId }
            break
        default:
            entityTypeSelected = null
            break
        }

        const params = {
            ...this.getBaseRequest(externalInfoParsed),
            ...{ delayType },
            ...entityTypeSelected
        }

        const response = await axios.get<CommonPersonnelResponse[]>(`/statisticAnalysis/dossiers/delayAndResponseTimes/${entityType?.toLowerCase()}/${timeMeasureType}/${responseTimeType}/personnel`, { params })
        return response.data
    }

    public getBaseRequest(filter: StatisticalAnalysisDelaysAndResponseTimeDossierExternalInfo): StatisticalAnalysisDelayAndResponseTimesDossierBaseRequest {
        return {
            startDate: encodeURIComponent(formatDatetimes(filter.startDate as Date)),
            endDate: encodeURIComponent(formatDatetimes(filter.endDate as Date)),
            laboratoryIds: (filter.laboratories as LaboratoryStatisticalAnalysisSidebarDateInput[])?.map((item: LaboratoryStatisticalAnalysisSidebarDateInput) => item.labId),
            laboratoryUserId: store.getters.labId,
            showValidatedCasesOnly: filter.onlyValidatedCases as boolean
        }
    }

    public detailExportTo(
        externalFilter: StatisticalAnalysisDelaysAndResponseTimeDossierExternalInfo,
        reportGridRequest: ReportGridRequest,
        groupByFields: string [],
        groupValueFilterRequests: StatisticalAnalysisDynamicObject[],
        caseTypeId: Nullable<number>,
        delayType: StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeModeSelectorEnum,
        filterTimeSheetId: number,
        infoDetail: StatisticalAnalysisTemporalFilterEnum,
        exportType: CommonExportType
    ): Promise<AxiosResponse> {
        let params = {
            ...{ infoDetail },
            ...{ filterTimeSheetId },
            ...{ caseTypeId },
            ...{ groupByFields },
            ...this.getBaseRequest(externalFilter),
            ...{ exportType }
        }

        if (externalFilter.typeDelaysAndResponseTimes?.id === StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeSelectorEnum.DELAY) {
            params = {
                ...params,
                ...{ delayType }
            }
        }

        const bodyParams = {
            gridRequest: {
                ...reportGridRequest
            },
            groupValueFilterRequests: groupValueFilterRequests
        }

        return axios.put(`statisticAnalysis/dossiers/delayAndResponseTimes/${externalFilter.typeSearch?.id.toLocaleLowerCase()}/${externalFilter.typeDelaysAndResponseTimes?.id}/${externalFilter.trackingDetailSelected?.key}/export`, bodyParams,
            { params, responseType: 'arraybuffer' }
        ).then((response: AxiosResponse) => response)
    }

    public detailExternalExportTo(
        externalFilter: StatisticalAnalysisDelaysAndResponseTimeDossierExternalInfo,
        reportGridRequest: ReportGridRequest,
        groupValueFilterRequest: StatisticalAnalysisDynamicObject,
        caseTypeId: Nullable<number>,
        delayType: StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeModeSelectorEnum,
        filterTimeSheetId: number,
        infoDetail: StatisticalAnalysisTemporalFilterEnum,
        exportType: CommonExportType
    ): Promise<AxiosResponse> {
        let params = {
            ...{ infoDetail },
            ...{ filterTimeSheetId },
            ...{ caseTypeId },
            ...this.getBaseRequest(externalFilter),
            ...{ exportType }
        }

        if (externalFilter.typeDelaysAndResponseTimes?.id === StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeSelectorEnum.DELAY) {
            params = {
                ...params,
                ...{ delayType }
            }
        }

        const bodyParams = {
            gridRequest: {
                ...reportGridRequest
            },
            groupValueFilterRequest
        }

        return axios.put(`statisticAnalysis/dossiers/delayAndResponseTimes/${externalFilter.typeSearch?.id.toLocaleLowerCase()}/${externalFilter.typeDelaysAndResponseTimes?.id}/${externalFilter.trackingDetailSelected?.key}/detailList/export`, bodyParams,
            { params, responseType: 'arraybuffer' }
        ).then((response: AxiosResponse) => response)
    }

    generateTimesheetLabelSerie(serie: StatisticalAnalysisTimesheetItemSerie): string {
        const tranlationTime = i18n.tc(`statisticalAnalysis.times.${serie.unit.toLocaleLowerCase()}`)
        if (serie.from && serie.to) {
            return `${serie.from} - ${serie.to} ${tranlationTime}`
        } else if (serie.from && !serie.to) {
            return `> ${serie.from} ${tranlationTime}`
        } else {
            return `< ${serie.to} ${tranlationTime}`
        }
    }

    private _parseChartData(filters: StatisticalAnalysisViewDetailChartFilters, response: StatisticalAnalysisDelayAndResponseTimesDossierDetailViewChart[]): StatisticalAnalysisViewDetailChartData {
        const sidebarFilter = filters?.externalFiltering.sidebarFilters as StatisticalAnalysisDelaysAndResponseTimeDossierExternalInfo
        const timeSeriesOrder = sidebarFilter.filterTimeSheetId?.timeSeries
        const categories = chain(timeSeriesOrder).map((filter:StatisticalAnalysisTimesheetItemSerie) => {
            return this.generateTimesheetLabelSerie(filter)
        }).value()
        const series:SeriesOptionsType[] = chain(timeSeriesOrder).reduce((aggr:StatisticalAnalysisDelayAndResponseTimesDossierDetailViewChart[], item: StatisticalAnalysisTimesheetItemSerie) => {
            const orderedItem = response.find((responseItem: StatisticalAnalysisDelayAndResponseTimesDossierDetailViewChart) => responseItem.timeSerie.id === item.id) as StatisticalAnalysisDelayAndResponseTimesDossierDetailViewChart
            aggr.push(orderedItem)
            return aggr
        }, [])
            .map('groupings').flatMap().groupBy('groupById').mapValues((value: any) => {
                return {
                    name: chain(value).map('groupBy').uniq().value().toString(),
                    data: map(value, 'total')
                }
            }).flatMap().value() as SeriesOptionsType[]
        return {
            categories,
            series
        }
    }
}

export const delayAndResponseTimesDossierService = new DelayAndResponseTimesDossierService()
